import { useState, useEffect, useCallback } from 'react';

/**
 * Кастомный хук для работы с Telegram WebApp
 * Обеспечивает инициализацию, обработку событий и методы управления
 */
export const useTelegram = () => {
  const [webApp, setWebApp] = useState(null);
  const [user, setUser] = useState(null);
  const [initData, setInitData] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isTelegramWebApp, setIsTelegramWebApp] = useState(false);

  // Инициализация Telegram WebApp
  useEffect(() => {
    const initTelegramWebApp = () => {
      // Проверяем, доступен ли Telegram WebApp
      if (window.Telegram && window.Telegram.WebApp) {
        const tg = window.Telegram.WebApp;
        
        // Инициализируем WebApp
        tg.ready();
        
        // Настраиваем основные параметры
        tg.expand(); // Расширяем на всю высоту
        tg.enableClosingConfirmation(); // Включаем подтверждение закрытия
        
        // Устанавливаем тему
        if (tg.colorScheme === 'dark') {
          document.documentElement.setAttribute('data-theme', 'dark');
        } else {
          document.documentElement.setAttribute('data-theme', 'light');
        }
        
        // Обработчик изменения viewport
        tg.onEvent('viewportChanged', () => {
          console.log('Viewport changed:', {
            viewportHeight: tg.viewportHeight,
            viewportStableHeight: tg.viewportStableHeight
          });
          
          // Обновляем высоту контейнера приложения
          const appContainer = document.querySelector('.app-container');
          if (appContainer) {
            appContainer.style.height = `${tg.viewportHeight}px`;
          }
        });
        
        // Обработчик изменения темы
        tg.onEvent('themeChanged', () => {
          const theme = tg.colorScheme === 'dark' ? 'dark' : 'light';
          document.documentElement.setAttribute('data-theme', theme);
          console.log('Theme changed to:', theme);
        });
        
        // Обработчик изменения размера окна
        tg.onEvent('mainButtonClicked', () => {
          console.log('Main button clicked');
        });
        
        // Обработчик нажатия кнопки "Назад"
        tg.onEvent('backButtonClicked', () => {
          console.log('Back button clicked');
          // Можно добавить логику навигации назад
        });
        
        // Получаем данные пользователя
        const userData = tg.initDataUnsafe?.user;
        const initDataString = tg.initData;
        
        if (userData && initDataString) {
          setUser(userData);
          setInitData(initDataString);
          setIsTelegramWebApp(true);
          console.log('Telegram WebApp initialized with user:', userData);
        } else {
          // Если нет данных пользователя, используем гостевого пользователя
          setUser({
            id: 0,
            first_name: 'Гость',
            last_name: 'Пользователь',
            username: null,
            telegram_id: 0
          });
          setIsTelegramWebApp(false);
          console.log('Using guest user for regular browser');
        }
        
        setWebApp(tg);
        setIsReady(true);
        
      } else {
        // Telegram WebApp недоступен - используем гостевого пользователя
        setUser({
          id: 0,
          first_name: 'Гость',
          last_name: 'Пользователь',
          username: null,
          telegram_id: 0
        });
        setIsTelegramWebApp(false);
        setIsReady(true);
        console.log('Telegram WebApp not available, using guest user');
      }
    };

    // Инициализируем после загрузки DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initTelegramWebApp);
    } else {
      initTelegramWebApp();
    }

    // Очистка при размонтировании
    return () => {
      if (webApp) {
        // Отключаем обработчики событий
        webApp.offEvent('viewportChanged');
        webApp.offEvent('themeChanged');
        webApp.offEvent('mainButtonClicked');
        webApp.offEvent('backButtonClicked');
      }
    };
  }, []);

  // Метод для закрытия приложения
  const closeApp = useCallback(() => {
    if (webApp && isTelegramWebApp) {
      webApp.close();
    } else {
      // Для обычного браузера показываем сообщение
      window.alert('Приложение закрыто');
    }
  }, [webApp, isTelegramWebApp]);

  // Метод для показа главной кнопки
  const showMainButton = useCallback((text, callback) => {
    if (webApp && isTelegramWebApp) {
      webApp.MainButton.text = text;
      webApp.MainButton.onClick(callback);
      webApp.MainButton.show();
    }
  }, [webApp, isTelegramWebApp]);

  // Метод для скрытия главной кнопки
  const hideMainButton = useCallback(() => {
    if (webApp && isTelegramWebApp) {
      webApp.MainButton.hide();
    }
  }, [webApp, isTelegramWebApp]);

  // Метод для показа кнопки "Назад"
  const showBackButton = useCallback((callback) => {
    if (webApp && isTelegramWebApp) {
      webApp.BackButton.onClick(callback);
      webApp.BackButton.show();
    }
  }, [webApp, isTelegramWebApp]);

  // Метод для скрытия кнопки "Назад"
  const hideBackButton = useCallback(() => {
    if (webApp && isTelegramWebApp) {
      webApp.BackButton.hide();
    }
  }, [webApp, isTelegramWebApp]);

  // Метод для показа уведомления
  const showAlert = useCallback((message) => {
    if (webApp && isTelegramWebApp) {
      webApp.showAlert(message);
    } else {
      window.alert(message);
    }
  }, [webApp, isTelegramWebApp]);

  // Метод для показа подтверждения
  const showConfirm = useCallback((message, callback) => {
    if (webApp && isTelegramWebApp) {
      webApp.showConfirm(message, callback);
    } else {
      const result = window.confirm(message);
      callback(result);
    }
  }, [webApp, isTelegramWebApp]);

  // Метод для получения данных для инициализации на бэкенде
  const getInitData = useCallback(() => {
    if (isTelegramWebApp && initData) {
      return {
        init_data: initData,
        user: user
      };
    }
    return null;
  }, [isTelegramWebApp, initData, user]);

  // Метод для получения telegram_id пользователя
  const getTelegramId = useCallback(() => {
    return user?.id || 0;
  }, [user]);

  // Метод для проверки, является ли пользователь гостем
  const isGuestUser = useCallback(() => {
    return !isTelegramWebApp || user?.id === 0;
  }, [isTelegramWebApp, user]);

  // Метод для получения полного имени пользователя
  const getUserFullName = useCallback(() => {
    if (user) {
      return `${user.first_name} ${user.last_name || ''}`.trim();
    }
    return 'Гость';
  }, [user]);

  // Метод для получения username пользователя
  const getUsername = useCallback(() => {
    return user?.username || null;
  }, [user]);

  // Метод для установки заголовка
  const setHeaderColor = useCallback((color) => {
    if (webApp && isTelegramWebApp) {
      webApp.setHeaderColor(color);
    }
  }, [webApp, isTelegramWebApp]);

  // Метод для установки цвета фона
  const setBackgroundColor = useCallback((color) => {
    if (webApp && isTelegramWebApp) {
      webApp.setBackgroundColor(color);
    }
  }, [webApp, isTelegramWebApp]);

  // Метод для включения/выключения подтверждения закрытия
  const enableClosingConfirmation = useCallback((enable = true) => {
    if (webApp && isTelegramWebApp) {
      if (enable) {
        webApp.enableClosingConfirmation();
      } else {
        webApp.disableClosingConfirmation();
      }
    }
  }, [webApp, isTelegramWebApp]);

  return {
    // Состояние
    webApp,
    user,
    initData,
    isReady,
    isTelegramWebApp,
    
    // Методы управления
    closeApp,
    showMainButton,
    hideMainButton,
    showBackButton,
    hideBackButton,
    showAlert,
    showConfirm,
    
    // Методы получения данных
    getInitData,
    getTelegramId,
    isGuestUser,
    getUserFullName,
    getUsername,
    
    // Методы настройки интерфейса
    setHeaderColor,
    setBackgroundColor,
    enableClosingConfirmation,
    
    // Утилиты
    viewportHeight: webApp?.viewportHeight || window.innerHeight,
    viewportStableHeight: webApp?.viewportStableHeight || window.innerHeight,
    colorScheme: webApp?.colorScheme || 'light',
    themeParams: webApp?.themeParams || {}
  };
}; 